//Update tailwing.config.ts safelists if you add new colors
export interface EventColorStyle {
  backgroundColor: string;
  color: string;
  borderColor: string;
}

export interface EventColor {
  color: string;
  textColor: string;
  borderColor: string;
  backgroundColor: string;
  /** True when the color is a custom hex value rather than a predefined Tailwind key. */
  isCustom: boolean;
  /** Inline styles, only present for custom hex colors (Tailwind classes can't be generated at runtime). */
  style?: EventColorStyle;
}

const SHADE_TEXT = '900';
const SHADE_BACKGROUND = '200';

/** A custom color is stored as a hex string (e.g. "#3b82f6") instead of a Tailwind color key. */
export function isCustomColor(color: string | undefined | null): boolean {
  return typeof color === 'string' && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(color.trim());
}

/** Returns a readable text color (dark or white) for a given hex background based on luminance. */
export function getContrastTextColor(hex: string): string {
  const normalized = hex.trim().replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;

  const r = parseInt(full.substring(0, 2), 16);
  const g = parseInt(full.substring(2, 4), 16);
  const b = parseInt(full.substring(4, 6), 16);

  // Perceived luminance (ITU-R BT.601).
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1f2937' : '#ffffff';
}
export const EventColors = {
  red: 'red',
  amber: 'amber',
  yellow: 'yellow',
  green: 'green',
  teal: 'teal',
  sky: 'sky',
  indigo: 'indigo',
  purple: 'purple',
};

export function getRandomColor(): string {
  const values = Object.values(EventColors);
  return values[Math.floor(Math.random() * values.length)];
}

export function getRandomEventColor(): EventColor {
  const color = getRandomColor();

  return createEventColor(color);
}

export function createEventColor(color: string): EventColor {
  if (isCustomColor(color)) {
    const hex = color.trim();
    return {
      color: hex,
      isCustom: true,
      textColor: '',
      borderColor: '',
      backgroundColor: '',
      style: {
        backgroundColor: hex,
        color: getContrastTextColor(hex),
        borderColor: hex,
      },
    };
  }

  return {
    color,
    isCustom: false,
    textColor: `!text-${color}-${SHADE_TEXT}`,
    borderColor: `!border-${color}-${SHADE_BACKGROUND}`,
    backgroundColor: `!bg-${color}-${SHADE_BACKGROUND}`,
  };
}

export function getAllColors(): string[] {
  return Object.values(EventColors);
}

/** Representative hex (the Tailwind -200 background shade) for each predefined color key. */
export const DEFAULT_COLOR_HEX: Record<string, string> = {
  red: '#fecaca',
  amber: '#fde68a',
  yellow: '#fef08a',
  green: '#bbf7d0',
  teal: '#99f6e4',
  sky: '#bae6fd',
  indigo: '#c7d2fe',
  purple: '#e9d5ff',
};

/** Returns the editable hex for a color key (the key itself for custom colors). */
export function getColorHex(colorKey: string): string {
  if (isCustomColor(colorKey)) {
    return colorKey.trim();
  }
  return DEFAULT_COLOR_HEX[colorKey] ?? '#cccccc';
}

export default EventColors;
